import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Copy, Plus, Trash2 } from 'lucide-react'
import {
  addExtractionProfileField,
  cloneExtractionProfile,
  deleteExtractionProfileField,
  extractionValueTypeLabels,
  extractionValueTypeToLabel,
  fetchBusinessEntities,
  fetchDivisions,
  fetchExtractionProfileById,
  updateExtractionProfileField,
  updateExtractionProfileScope,
  updateExtractionProfileStatus,
  type ExtractionValueTypeLabel,
} from '../services/api'
import { EmptyState, MimCard, MimPage, StatusPill } from '../components/MimUi'

const EMPTY_FIELD = {
  canonicalFieldKey: '',
  displayName: '',
  aliases: '',
  valueType: 'Text' as ExtractionValueTypeLabel,
  unit: '',
  requiredOnWebsite: false,
  claimCheckRole: 'MustNotContradict',
}

const claimRoleOptions = [
  { value: 'OutOfScope', label: 'Out of scope' },
  { value: 'MustNotContradict', label: 'Must not contradict' },
  { value: 'MustMatch', label: 'Must match' },
]

export default function ExtractionProfileDetail() {
  const { profileId } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [fieldForm, setFieldForm] = useState(EMPTY_FIELD)
  const [cloneVersion, setCloneVersion] = useState('')
  const [showClone, setShowClone] = useState(false)
  const [scopeDivisionId, setScopeDivisionId] = useState('')
  const [scopeBusinessEntityId, setScopeBusinessEntityId] = useState('')

  const { data: profile, isLoading } = useQuery({
    queryKey: ['extraction-profile', profileId],
    queryFn: () => fetchExtractionProfileById(profileId!),
    enabled: !!profileId,
  })

  const { data: divisions } = useQuery({ queryKey: ['org', 'divisions'], queryFn: fetchDivisions })
  const { data: businessEntities } = useQuery({ queryKey: ['org', 'business-entities'], queryFn: () => fetchBusinessEntities() })

  useEffect(() => {
    if (!profile) return
    setScopeDivisionId(profile.divisionId ?? '')
    setScopeBusinessEntityId(profile.businessEntityId ?? '')
  }, [profile])

  const canEdit = profile?.status === 'Draft'

  const scopeMutation = useMutation({
    mutationFn: () =>
      updateExtractionProfileScope(profileId!, {
        divisionId: scopeBusinessEntityId ? undefined : (scopeDivisionId || undefined),
        businessEntityId: scopeBusinessEntityId || undefined,
        clearScope: !scopeDivisionId && !scopeBusinessEntityId,
      }),
    onSuccess: async () => {
      setMessage('Scope updated.')
      await invalidate()
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to update scope.')),
  })

  async function invalidate() {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['extraction-profile', profileId] }),
      qc.invalidateQueries({ queryKey: ['extraction-profiles', 'rules-engine'] }),
    ])
  }

  const clearFeedback = () => {
    setMessage('')
    setError('')
  }

  const addFieldMutation = useMutation({
    mutationFn: () =>
      addExtractionProfileField(profileId!, {
        canonicalFieldKey: fieldForm.canonicalFieldKey.trim(),
        displayName: fieldForm.displayName.trim() || undefined,
        aliases: fieldForm.aliases.split(',').map((a) => a.trim()).filter(Boolean),
        valueType: fieldForm.valueType,
        unit: fieldForm.unit.trim() || undefined,
        requiredOnWebsite: fieldForm.claimCheckRole === 'MustMatch',
        claimCheckRole: fieldForm.claimCheckRole,
      }),
    onSuccess: async () => {
      setFieldForm(EMPTY_FIELD)
      setShowAdd(false)
      setMessage('Field added.')
      await invalidate()
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to add field.')),
  })

  const deleteFieldMutation = useMutation({
    mutationFn: (fieldId: string) => deleteExtractionProfileField(profileId!, fieldId),
    onSuccess: async () => {
      setMessage('Field removed.')
      await invalidate()
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to remove field.')),
  })

  const claimRoleMutation = useMutation({
    mutationFn: (vars: { fieldId: string; claimCheckRole: string }) =>
      updateExtractionProfileField(profileId!, vars.fieldId, {
        claimCheckRole: vars.claimCheckRole,
        requiredOnWebsite: vars.claimCheckRole === 'MustMatch',
      }),
    onSuccess: invalidate,
    onError: (err) => setError(getErrorMessage(err, 'Failed to update field.')),
  })

  const statusMutation = useMutation({
    mutationFn: (status: 'Approved' | 'Retired') => updateExtractionProfileStatus(profileId!, status),
    onSuccess: async (_result, status) => {
      setMessage(status === 'Approved' ? 'Extraction profile approved.' : 'Extraction profile retired.')
      await invalidate()
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to update status.')),
  })

  const cloneMutation = useMutation({
    mutationFn: () => cloneExtractionProfile(profileId!, cloneVersion.trim()),
    onSuccess: async (created) => {
      setShowClone(false)
      setCloneVersion('')
      await qc.invalidateQueries({ queryKey: ['extraction-profiles', 'rules-engine'] })
      navigate(`/extraction-profiles/${created.id}`)
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to clone extraction profile.')),
  })

  const handleAddField = () => {
    clearFeedback()
    if (!fieldForm.canonicalFieldKey.trim()) {
      setError('Canonical field key is required.')
      return
    }
    addFieldMutation.mutate()
  }

  const handleClone = () => {
    clearFeedback()
    if (!cloneVersion.trim()) {
      setError('A new version is required.')
      return
    }
    cloneMutation.mutate()
  }

  if (isLoading) return <MimPage title="Extraction Profile"><EmptyState title="Loading extraction profile" /></MimPage>
  if (!profile) return <MimPage title="Extraction Profile"><EmptyState title="Extraction profile not found" /></MimPage>

  return (
    <MimPage
      title={profile.name}
      subtitle={`Version ${profile.version} - defines the field aliases the extractor looks for on target pages`}
      actions={<button type="button" className="mim-btn mim-btn-secondary" onClick={() => navigate('/rules-engine')}>&larr; Back to Rules Engine</button>}
    >
      <div className="mim-stack">
        {message && <div className="rules-feedback rules-feedback-success">{message}</div>}
        {error && <div className="rules-feedback rules-feedback-error">{error}</div>}

        <MimCard
          title="Status"
          actions={(
            <div className="mim-toolbar">
              <StatusPill status={profile.status} />
              {profile.status === 'Draft' && (
                <button type="button" className="mim-btn mim-btn-primary" disabled={statusMutation.isPending} onClick={() => statusMutation.mutate('Approved')}>
                  <CheckCircle2 size={14} /> Approve
                </button>
              )}
              {profile.status !== 'Retired' && (
                <button type="button" className="mim-btn mim-btn-secondary" disabled={statusMutation.isPending} onClick={() => statusMutation.mutate('Retired')}>
                  Retire
                </button>
              )}
              <button type="button" className="mim-btn mim-btn-secondary" onClick={() => setShowClone((current) => !current)}>
                <Copy size={14} /> Clone as new draft
              </button>
            </div>
          )}
        >
          {!canEdit && (
            <div className="mim-note">
              {profile.status === 'Approved'
                ? 'Approved extraction profiles are immutable. Clone this profile into a new draft to change its fields.'
                : 'Retired extraction profiles are read-only.'}
            </div>
          )}
          {showClone && (
            <div className="mim-toolbar" style={{ marginTop: 12 }}>
              <input
                className="mim-input"
                placeholder="New version, e.g. 1.1.0"
                value={cloneVersion}
                onChange={(e) => setCloneVersion(e.target.value)}
                style={{ maxWidth: 200 }}
              />
              <button type="button" className="mim-btn mim-btn-primary" disabled={cloneMutation.isPending} onClick={handleClone}>
                Create draft copy
              </button>
            </div>
          )}
        </MimCard>

        <MimCard
          title="Scope"
          subtitle="Most-specific wins: a Business Entity match beats a Division match, which beats the unscoped global default. A product only picks up a scoped profile if it belongs to that Division/Business Entity - otherwise it falls back to whichever unscoped profile is most recently approved."
        >
          <div className="mim-grid-2">
            <label>
              <span className="mim-label">Division</span>
              <select
                className="mim-select"
                value={scopeDivisionId}
                disabled={!canEdit}
                onChange={(e) => { setScopeDivisionId(e.target.value); setScopeBusinessEntityId('') }}
              >
                <option value="">Global (no division)</option>
                {divisions?.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            <label>
              <span className="mim-label">Business entity (more specific than division)</span>
              <select
                className="mim-select"
                value={scopeBusinessEntityId}
                disabled={!canEdit}
                onChange={(e) => setScopeBusinessEntityId(e.target.value)}
              >
                <option value="">None</option>
                {businessEntities?.map((be) => <option key={be.id} value={be.id}>{be.name}</option>)}
              </select>
            </label>
          </div>
          {canEdit && (
            <div className="mim-toolbar" style={{ marginTop: 12 }}>
              <button type="button" className="mim-btn mim-btn-primary" disabled={scopeMutation.isPending} onClick={() => { clearFeedback(); scopeMutation.mutate() }}>
                Save scope
              </button>
            </div>
          )}
        </MimCard>

        <MimCard
          title="Fields"
          subtitle="Aliases and section hints help the extractor recognize a field even when a website uses different wording than your canonical field name."
          actions={canEdit ? (
            <button type="button" className="mim-btn mim-btn-primary" onClick={() => { clearFeedback(); setShowAdd((current) => !current) }}>
              <Plus size={14} /> Add Field
            </button>
          ) : undefined}
        >
          {showAdd && canEdit && (
            <div className="mim-stack" style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border-color, #e2e2e2)' }}>
              <div className="mim-grid-2">
                <label>
                  <span className="mim-label">Canonical Field Key *</span>
                  <input className="mim-input" value={fieldForm.canonicalFieldKey} onChange={(e) => setFieldForm((c) => ({ ...c, canonicalFieldKey: e.target.value }))} placeholder="e.g. thermal_resistance" />
                </label>
                <label>
                  <span className="mim-label">Display Name</span>
                  <input className="mim-input" value={fieldForm.displayName} onChange={(e) => setFieldForm((c) => ({ ...c, displayName: e.target.value }))} placeholder="e.g. Thermal Resistance" />
                </label>
                <label>
                  <span className="mim-label">Aliases (comma-separated)</span>
                  <input className="mim-input" value={fieldForm.aliases} onChange={(e) => setFieldForm((c) => ({ ...c, aliases: e.target.value }))} placeholder="e.g. R-value, Thermal resistance (R)" />
                  <span className="mim-help-text">Alternate wording the extractor should also match on a webpage.</span>
                </label>
                <label>
                  <span className="mim-label">Value Type</span>
                  <select className="mim-select" value={fieldForm.valueType} onChange={(e) => setFieldForm((c) => ({ ...c, valueType: e.target.value as ExtractionValueTypeLabel }))}>
                    {extractionValueTypeLabels.map((type) => <option key={type} value={type}>{type}</option>)}
                  </select>
                </label>
                <label>
                  <span className="mim-label">Unit</span>
                  <input className="mim-input" value={fieldForm.unit} onChange={(e) => setFieldForm((c) => ({ ...c, unit: e.target.value }))} placeholder="Optional, e.g. m2K/W" />
                </label>
                <label>
                  <span className="mim-label">Check</span>
                  <select className="mim-select" value={fieldForm.claimCheckRole} onChange={(e) => setFieldForm((c) => ({ ...c, claimCheckRole: e.target.value, requiredOnWebsite: e.target.value === 'MustMatch' }))}>
                    {claimRoleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
              </div>
              <div className="mim-toolbar">
                <button type="button" className="mim-btn mim-btn-primary" disabled={addFieldMutation.isPending} onClick={handleAddField}>Save Field</button>
                <button type="button" className="mim-btn mim-btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </div>
          )}

          {profile.fields.length === 0 ? (
            <EmptyState title="No fields defined" detail={canEdit ? 'Use Add Field to define the first field.' : 'This profile has no field definitions.'} />
          ) : (
            <div className="mim-table-wrap">
              <table className="mim-table">
                <thead>
                  <tr>
                    <th>Canonical Key</th>
                    <th>Display Name</th>
                    <th>Aliases</th>
                    <th>Value Type</th>
                    <th>Unit</th>
                    <th>Check</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {[...profile.fields].sort((a, b) => a.sortOrder - b.sortOrder).map((field) => (
                    <tr key={field.id}>
                      <td><span className="mim-mono">{field.canonicalFieldKey}</span></td>
                      <td>{field.displayName || '-'}</td>
                      <td>{parseAliases(field.aliasesJson).join(', ') || '-'}</td>
                      <td>{extractionValueTypeToLabel(field.valueType)}</td>
                      <td>{field.unit || '-'}</td>
                      <td>
                        <select
                          className="mim-select"
                          value={field.claimCheckRole || (field.requiredOnWebsite ? 'MustMatch' : 'MustNotContradict')}
                          disabled={!canEdit || claimRoleMutation.isPending}
                          onChange={(e) => claimRoleMutation.mutate({ fieldId: field.id, claimCheckRole: e.target.value })}
                        >
                          {claimRoleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="mim-icon-button"
                          disabled={!canEdit || deleteFieldMutation.isPending}
                          title={!canEdit ? 'Only draft profiles can be edited' : 'Delete field'}
                          onClick={() => { if (confirm(`Delete field "${field.canonicalFieldKey}"?`)) deleteFieldMutation.mutate(field.id) }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </MimCard>
      </div>
    </MimPage>
  )
}

function parseAliases(aliasesJson?: string): string[] {
  if (!aliasesJson) return []
  try {
    const parsed = JSON.parse(aliasesJson) as unknown
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

function getErrorMessage(err: unknown, fallback: string) {
  const responseData = (err as { response?: { data?: unknown } })?.response?.data
  if (typeof responseData === 'string') return responseData
  if (responseData && typeof responseData === 'object' && 'message' in responseData) {
    const message = (responseData as { message?: unknown }).message
    if (typeof message === 'string') return message
  }
  const message = (err as { message?: unknown })?.message
  return typeof message === 'string' ? message : fallback
}
